import React from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import classNames from 'classnames';

/**
 * LinkButton
 * 
 * A React Router Link styled as a button with optional icon.
 * 
 * Props:
 *  - to: Route path to navigate to
 *  - icon: Bootstrap icon class (e.g., "bi-arrow-right")
 *  - iconPosition: 'start' | 'end' (default: 'end')
 *  - variant: 'primary' | 'purple' | 'success' | 'secondary' | 'outline-primary' etc.
 *  - size: 'sm' | 'md' | 'lg' (default: 'md')
 *  - className: Additional CSS classes
 *  - children: Button text
 */
export default function LinkButton({
  to,
  icon,
  iconPosition = 'end',
  variant = 'primary',
  size,
  className = '',
  children,
  state,
  ...linkProps
}) {
  // Map custom variants to CSS classes
  const variantClassMap = {
    purple: 'link-btn-purple',
    primary: 'btn-primary',
    secondary: 'btn-secondary',
    success: 'btn-success',
    'outline-primary': 'btn-outline-primary',
    'outline-secondary': 'btn-outline-secondary',
  };

  const variantClass = variantClassMap[variant] || `btn-${variant}`;
  const isCustomVariant = variant === 'purple';

  const sizeClass = size === 'sm' ? 'btn-sm' : size === 'lg' ? 'btn-lg' : '';

  const linkClasses = classNames(
    'btn',
    'd-inline-flex',
    'align-items-center',
    'justify-content-center',
    'gap-2',
    {
      [variantClass]: !isCustomVariant,
      'link-btn-purple': isCustomVariant,
      [sizeClass]: sizeClass,
    },
    className
  );

  return (
    <Link to={to} className={linkClasses} state={state} {...linkProps}>
      {icon && iconPosition === 'start' && <i className={`bi ${icon}`} />}
      {children}
      {icon && iconPosition === 'end' && <i className={`bi ${icon}`} />}
    </Link>
  );
}

LinkButton.propTypes = {
  to: PropTypes.string.isRequired,
  icon: PropTypes.string,
  iconPosition: PropTypes.oneOf(['start', 'end']),
  variant: PropTypes.string,
  size: PropTypes.oneOf(['sm', 'md', 'lg']),
  className: PropTypes.string,
  children: PropTypes.node.isRequired,
  state: PropTypes.object,
};
